// Firebase client configuration for the PWA.
//
// These values are NOT secrets — they are designed to ship in client bundles.
// Real protection comes from:
//   1. Firestore Security Rules (firestore.rules)
//   2. Firebase App Check (Play Integrity for Android, reCAPTCHA Enterprise for web)
//   3. API key restrictions in Google Cloud Console (HTTP referrer, Android SHA-1)
//
// Server secrets (Admin SDK service account, third-party API keys) live in
// Google Secret Manager and are accessed only by Cloud Run.

import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";

export const firebaseConfig = {
  apiKey: "AIzaSyAOo3GwNOCWiNUjP_0cYLxuRl12wqSrQDo",
  authDomain: "winnersden-55816.firebaseapp.com",
  databaseURL: "https://winnersden-55816-default-rtdb.firebaseio.com",
  projectId: "winnersden-55816",
  storageBucket: "winnersden-55816.appspot.com",
  messagingSenderId: "441810944855",
  appId: "1:441810944855:web:0dbb57ee9429618d297412",
};

export const app = initializeApp(firebaseConfig);

// App Check must be initialized before any other Firebase service is called.
// Set VITE_RECAPTCHA_SITE_KEY in your build environment, or set
// VITE_DISABLE_APP_CHECK=1 to skip init entirely (useful for local dev where
// the reCAPTCHA Enterprise site key hasn't been authorized for localhost).
//
// **Domain config:** the site key must list every Hosting domain it serves
// in the GCP Console → Security → reCAPTCHA Enterprise → Key → Allowed
// domains. Forgetting this throws `appCheck/recaptcha-error` on every page
// load. The error is swallowed by api/client.ts so requests still succeed
// when ENFORCE_APP_CHECK=0, but it spams the console.
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const disableAppCheck = import.meta.env.VITE_DISABLE_APP_CHECK === "1";

function initAppCheckSafe(): AppCheck | null {
  if (disableAppCheck || !recaptchaSiteKey) return null;
  try {
    return initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Init can throw if the SDK can't reach Google's reCAPTCHA bootstrap or
    // the site key is malformed. Swallow rather than crash the whole shell —
    // the request layer (api/client.ts) already handles missing tokens.
    // eslint-disable-next-line no-console
    console.warn("[app-check] init failed; continuing without App Check tokens", err);
    return null;
  }
}

export const appCheck: AppCheck | null = initAppCheckSafe();

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

export const messagingPromise = isMessagingSupported().then((supported) =>
  supported ? getMessaging(app) : null,
);
